import Button from "@mui/material/Button";
import "./exceptionDetails.css";
import React, { useEffect, useState } from "react";
import DynamicGrid from "../../components/dynamicGrid/DynamicGrid";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccordionDetails from "@mui/material/AccordionDetails";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import {
  APP,
  AppURLs,
  DAMAGE_OSD_TYPE,
  EOsdType,
  SHORTAGE_OSD_TYPES,
} from "../../constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import get from "lodash/get";
import { IProImage } from "../../models/master";
import { useDispatch, useSelector } from "react-redux";
import { getProImages } from "../../API/getProImages";
import DialogImage from "../../components/popupImage/popupImage";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import { GridColDef } from "@mui/x-data-grid";
import Loader from "../../components/loader/loader";
import { DateHelper } from "../../helpers";
import AppState from "../../AppState";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import { exceptionDetails } from "../../API/exceptionDetails";
import { IOsdDetails } from "../../models/osd";
import { useApiHeaders } from "../../@hooks/useApiHeaders";

function ExceptionDetails() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tabPosition } = useSelector((state: AppState) => ({
    tabPosition: state.tabPosition,
  }));

  const urls: string[] = [
    AppURLs.searchException,
    AppURLs.myAssignments,
    AppURLs.overages,
    AppURLs.shortages,
    AppURLs.damages,
    AppURLs.onHandDelivery,
    AppURLs.cleared
  ];

  const columns: GridColDef[] = APP.overage.overageDetailsColumns;
  const shipperDetails: GridColDef[] = APP.overage.overagesShipperDetails;
  const consigneeDetails: GridColDef[] = APP.overage.overageConsigneeDetails;
  const [exception, setException] = useState<IOsdDetails>({});
  const [proImages, setProImages] = useState<IProImage[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [pageLoading, setPageLoading] = useState<boolean>(false);

  const param = useParams();
  const isShowMailButton = [EOsdType.bnf, EOsdType.dd, EOsdType.oh].includes(
    (exception?.osdType?.toUpperCase().trim() as EOsdType) ?? EOsdType.dd,
  );

  const apiHeaders = useApiHeaders({
    sectionName: "osdDescription",
    osdType: exception?.osdType,
    osdNumber: param.id,
  });

  const navigates = useNavigate();

  const handleSendDisposition = () => {
    navigates(
      AppURLs.sendDisposition.replace(
        ":osdNumber",
        exception?.osdNumber?.toString() ?? "",
      ),
    );
  };

  // Send APB mail
  const sendAPBMail = () => {
    navigate(AppURLs.sendAPB.replace(":osdNumber", param.id!));
  };

  const columnVisibilityModel = React.useMemo(() => {
    if (exception?.osdType?.trim()?.toUpperCase() !== EOsdType.nb) {
      return {
        proNumber: true,
      };
    }
    return {
      proNumber: false,
    };
  }, [exception?.osdType]);

  // Get Exception Details by exception number
  const getExceptionDetails = () => {
    setPageLoading(true);
    exceptionDetails(param.id, apiHeaders)
      .then(
        (data) => {
          setPageLoading(false);

          if (
            [...SHORTAGE_OSD_TYPES, ...DAMAGE_OSD_TYPE].includes(
              data.osdType as EOsdType,
            )
          ) {
            Object.assign(data, {
              od400ProNumber: undefined,
              od400Completed: "",
              od400Manifest: "",
              od400WhereFound: "",
              od400Trailer: "",
              od400Bay: "",
            });
          }

          (["manifest", "od400Manifest"] as Array<keyof IOsdDetails>).forEach(
            (key) => {
              // eslint-disable-next-line eqeqeq
              if (data[key] == 0) {
                data[key] = undefined;
              }
            },
          );

          // merge address1 and address2 to address for Shipper and Consign
          if (get(data, "osdNumber", "") !== "") {
            data["shipperAddress"] =
              `${data?.shipperAddress1} ${data?.shipperAddress2}`;
            data["consigneeAddress"] =
              `${data?.consigneeAddress1} ${data?.consigneeAddress2}`;
            setException(data);
          }
        },
        (err) => {
          setPageLoading(false);
          console.error(err);
        },
      )
      .catch((error) => {
        setPageLoading(false);
        console.error(error);
      });
  };

  const isDispositionType = (osdType: EOsdType) => {
    return [EOsdType.pr, EOsdType.rd, EOsdType.rf].includes(osdType);
  };

  const getAS400Images = () => {
    // check pro is valid or not
    const osdNumber = exception.osdNumber ?? param.id;
    if (osdNumber) {
      setPageLoading(true);
      const param = {
        osdNumber,
      };
      // Get Pro Images from API
      getProImages(param, apiHeaders)
        .then((response) => {
          setPageLoading(false);
          const sortImages = get(response, "data", []).sort(
            (a: IProImage, b: IProImage) =>
              a.imageFilename?.localeCompare(b.imageFilename),
          );

          if (sortImages.length === 0 && response?.errorDetails?.length > 0) {
            dispatch(
              PopoutNotification(
                NOTIFICATION_TYPE.ERROR,
                response?.errorDetails[0].error ??
                  "Something went wrong. Please try again later.",
                Date.now().toString(),
              ),
            );
            return;
          }
          setProImages(sortImages);
          if (response.data.length > 0) {
            setIsOpen(!isOpen);
          } else {
            dispatch(
              PopoutNotification(
                NOTIFICATION_TYPE.INFO,
                "No image available",
                Date.now().toString(),
              ),
            );
          }
        })
        .catch((error) => {
          setPageLoading(false);
          dispatch(
            PopoutNotification(
              NOTIFICATION_TYPE.ERROR,
              typeof error === "string" ? error : "Image API error",
              Date.now().toString(),
            ),
          );
        });
    } else {
      dispatch(
        PopoutNotification(
          NOTIFICATION_TYPE.WARNING,
          "Selected OSD number is not valid",
          Date.now().toString(),
        ),
      );
    }
  };

  useEffect(() => {
    getExceptionDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [param.id]);

  return (
    <div className="bg-layout">
      {/* header */}
      <div className="header-section">
        <div className="back-section">
          <div className="back">
            <Link to={urls[tabPosition] ?? urls[1]}>
              <ArrowLeftIcon />
            </Link>
          </div>
          <div className="page-hader-content">
            <div className="page-header">OSD Description</div>
            <div className="page-subtitle">
              Last update:{" "}
              {exception?.lastUpdatedDate && exception?.lastUpdatedTime
                ? (() => {
                    const lastUpdatedDate =
                      exception?.lastInvUpdatedDate &&
                      exception?.lastInvUpdatedDate > exception?.lastUpdatedDate
                        ? exception?.lastInvUpdatedDate
                        : exception?.lastUpdatedDate;

                    const lastUpdatedTime =
                      exception?.lastInvUpdatedDate &&
                      exception?.lastInvUpdatedTime &&
                      (exception?.lastInvUpdatedDate >
                        exception?.lastUpdatedDate ||
                        (exception?.lastInvUpdatedDate ===
                          exception?.lastUpdatedDate &&
                          exception?.lastInvUpdatedTime >
                            exception?.lastUpdatedTime))
                        ? exception?.lastInvUpdatedTime
                        : exception?.lastUpdatedTime;

                    return DateHelper.convertDateTimeFromDB(
                      DateHelper.convertFromAS400Date(
                        JSON.stringify(lastUpdatedDate),
                      ),
                      DateHelper.convertFromAS400Time(
                        JSON.stringify(lastUpdatedTime),
                      ),
                    );
                  })()
                : "NA"}
            </div>
          </div>
        </div>
        <div className="edit-section">
          <Button
            variant="contained"
            onClick={getAS400Images}
            className={`entry ${isShowMailButton ? "mr-20" : ""}`}
          >
            View Image AS400
          </Button>
          {!isShowMailButton && (
            <Button
              variant="contained"
              onClick={() => {
                if (isDispositionType(exception?.osdType?.trim() as EOsdType)) {
                  handleSendDisposition();
                } else {
                  sendAPBMail();
                }
              }}
              className="assign"
            >
              {isDispositionType(exception?.osdType?.trim() as EOsdType)
                ? "Send Disposition"
                : "Send APB"}
            </Button>
          )}
        </div>
      </div>

      {/* body */}
      <div className="content-area osd-description">
        <DynamicGrid
          id={"osdNumber"}
          checkboxSelection={false}
          rows={exception?.osdNumber ? [exception] : []}
          columns={columns}
          hideFooter={true}
          disableColumnMenu={true}
          columnVisibilityModel={columnVisibilityModel}
        ></DynamicGrid>

        <Accordion className="table-accordion" defaultExpanded>
          <AccordionSummary
            className="des-header"
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
          >
            <Grid container>
              <Grid item md={6} xs={6}>
                <Typography className="accordion-header">
                  Shipper Details
                </Typography>
              </Grid>
              <Grid item md={6} xs={6}>
                <Typography className="accordion-header consignee-border">
                  Consignee Details
                </Typography>
              </Grid>
            </Grid>
          </AccordionSummary>
          <AccordionDetails className="details-accordion">
            <Grid container>
              <Grid item md={6} xs={6} className="border-right">
                <DynamicGrid
                  id={"shipperName"}
                  checkboxSelection={false}
                  rows={exception?.osdNumber ? [exception] : []}
                  columns={shipperDetails}
                  hideFooter={true}
                  disableColumnMenu={true}
                ></DynamicGrid>
              </Grid>
              <Grid item md={6} xs={6}>
                <DynamicGrid
                  id={"consigneeName"}
                  checkboxSelection={false}
                  rows={exception?.osdNumber ? [exception] : []}
                  columns={consigneeDetails}
                  hideFooter={true}
                  disableColumnMenu={true}
                ></DynamicGrid>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
        <Accordion className="table-accordion" defaultExpanded>
          <AccordionSummary
            className="des-header"
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel2bh-content"
            id="panel2bh-header"
          >
            <Grid item md={6} xs={6}>
              <Typography className="accordion-header">More Details</Typography>
            </Grid>
          </AccordionSummary>
          <AccordionDetails className="more-details">
            <Grid container spacing={2}>
              <Grid item lg={3} md={3} sm={6} className="background-box pb-0">
                <div className="grey-box">
                  <div className="content-box">
                    <div className="label">OD400 Completed</div>
                    <div className="value">
                      {exception?.["od400Completed"]}{" "}
                    </div>
                  </div>
                  <div className="content-box">
                    <div className="label">OD400 PRO #</div>
                    <div className="value">{exception?.["od400ProNumber"]}</div>
                  </div>
                  <div className="content-box">
                    <div className="label">OD400 Manifest</div>
                    <div className="value">{exception?.["od400Manifest"]}</div>
                  </div>
                  <div className="content-box">
                    <div className="label">OD400 Found</div>
                    <div className="value">
                      {exception?.["od400WhereFound"]}
                    </div>
                  </div>
                  <div className="content-box">
                    <div className="label">OD400 Trailer</div>
                    <div className="value">{exception?.["od400Trailer"]}</div>
                  </div>
                </div>
              </Grid>

              <Grid item lg={3} md={3} sm={6} className="background-box pb-0">
                <div className="grey-box">
                  <div className="content-box">
                    <div className="label">OD400 Bay</div>
                    <div className="value">{exception?.["od400Bay"]}</div>
                  </div>
                  <div className="content-box">
                    <div className="label">BL #</div>
                    <div className="value">{exception?.["blNumber"]}</div>
                  </div>
                  <div className="content-box">
                    <div className="label">PO #</div>
                    <div className="value">{exception?.["poNumber"]}</div>
                  </div>
                  <div className="content-box">
                    <div className="label">SN #</div>
                    <div className="value">{exception?.["snNumber"]}</div>
                  </div>
                  <div className="content-box">
                    <div className="label">Commodity</div>
                    <div
                      className="value text-overflow"
                      title={exception?.["commodity"]}
                    >
                      {exception?.["commodity"]}
                    </div>
                  </div>
                </div>
              </Grid>

              <Grid item lg={3} md={3} sm={6} className="background-box pb-0">
                <div className="grey-box">
                  <div className="content-box">
                    <div className="label">Package</div>
                    <div className="value">{exception?.["package"]}</div>
                  </div>
                  <div className="content-box">
                    <div className="label">Sum of Pieces</div>
                    <div className="value">{exception?.["pieces"]}</div>
                  </div>
                  <div className="content-box">
                    <div className="label">Sum of Weight</div>
                    <div className="value">{exception?.["weight"]}</div>
                  </div>
                  <div className="content-box">
                    <div className="label">Sum of # Pallets</div>
                    <div className="value">
                      {exception?.["numberOfPallets"]}
                    </div>
                  </div>
                  <div className="content-box">
                    <div className="label">Icm</div>
                    <div
                      className="value text-overflow"
                      title={exception?.["icm"]}
                    >
                      {exception?.["icm"]}
                    </div>
                  </div>
                </div>
              </Grid>

              <Grid item lg={3} md={3} sm={6} className="background-box pb-0">
                <div className="grey-box">
                  <div className="content-box">
                    <div className="label">SW ok</div>
                    <div className="value">{exception?.["swOk"]}</div>
                  </div>
                  <div className="content-box">
                    <div className="label">Manufacturer/Brand</div>
                    <div
                      className="value text-overflow"
                      title={exception?.["manufacturerBrand"]}
                    >
                      {exception?.["manufacturerBrand"]}
                    </div>
                  </div>
                  <div className="content-box">
                    <div className="label">Value Range</div>
                    <div className="value">{exception?.["valueRange"]}</div>
                  </div>
                  <div className="content-box">
                    <div className="label">Sum of Value &#62; $1k</div>
                    <div className="value">{exception?.["value1k"]}</div>
                  </div>
                  <div className="content-box">
                    <div className="label">H/U</div>
                    <div className="value">{exception?.["handlingUnits"]}</div>
                  </div>
                </div>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item lg={9} md={9} xs={9} className="background-box">
                <div className="grey-box">
                  <div className="content-box">
                    <div className="label">Descriptions</div>
                  </div>
                  <div className="content-box">
                    <div className="value">{exception?.description}</div>
                  </div>
                </div>
              </Grid>

              <Grid item lg={3} md={3} xs={3} className="background-box">
                <div className="grey-box remarks">
                  <div className="content-box">
                    <div className="label">Remarks</div>
                  </div>
                  <div className="content-box">
                    <div className="value">{exception?.remarks}</div>
                  </div>
                </div>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </div>
      <div>
        <DialogImage
          isDialogOpened={isOpen}
          handleCloseDialog={() => setIsOpen(false)}
          key={isOpen.toString()}
          imageData={proImages}
          excNumber={
            exception?.osdNumber ? exception?.osdNumber.toString() : ""
          }
        />
      </div>
      <div>
        <Loader pageLoader={pageLoading}></Loader>
      </div>
    </div>
  );
}

export default ExceptionDetails;
